import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, FileText, Paperclip, CheckCircle2, Circle, Clock, Server,
  Receipt, ListChecks, MessageSquareText, Sparkles, Loader2, ExternalLink,
  Download, AlertTriangle, Coins, Users, Code2,
} from "lucide-react";
import toast from "react-hot-toast";
import { axiosInstance } from "../lib/axios";
import { useAuthStore } from "../store/useAuthStore";
import PrdSection from "../components/prd/PrdSection";

/**
 * Everything stored against one project, on one page.
 *
 * Built because the project view showed about fifteen fields while the record
 * held several hundred — so work went in and nobody could confirm it had landed.
 * Nothing here is summarised away: both milestone sets with their deliverables,
 * every checklist item with its history, every document with a link that opens,
 * the questionnaire answers as the client wrote them, invoices, commissions,
 * the sync history, and the exact payload ERP receives.
 *
 * That last one matters most. The payload is built by the same function that
 * sends it, so what is shown here cannot drift from what ERP actually gets.
 */

const fmt = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";
const fmtDT = (d?: string | null) => (d ? new Date(d).toLocaleString("en-GB") : "—");
const money = (n: number, c = "USD") => {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: c, maximumFractionDigits: 0 }).format(n || 0);
  } catch {
    return `${c} ${(n || 0).toLocaleString()}`;
  }
};
const kb = (n: number) => (n >= 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

const Section = ({
  icon: Icon,
  title,
  meta,
  children,
}: {
  icon: any;
  title: string;
  meta?: string;
  children: React.ReactNode;
}) => (
  <section className="bg-surface-container-lowest border border-outline-variant rounded-xl overflow-hidden">
    <div className="px-4 py-3 border-b border-outline-variant flex items-center gap-2 bg-surface-container-low/40">
      <Icon size={15} className="text-primary" />
      <h2 className="text-[13px] font-bold text-on-surface">{title}</h2>
      {meta && <span className="text-[11px] text-on-surface-variant">· {meta}</span>}
    </div>
    <div className="p-4">{children}</div>
  </section>
);

const Field = ({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) => (
  <div className="min-w-0">
    <div className="text-[10px] uppercase tracking-wider font-bold text-on-surface-variant">{label}</div>
    <div className={`text-[13px] text-on-surface mt-0.5 break-words ${mono ? "font-mono text-[11px]" : ""}`}>
      {value === "" || value === null || value === undefined ? "—" : value}
    </div>
  </div>
);

const Pill = ({ children, tone = "neutral" }: { children: React.ReactNode; tone?: "neutral" | "good" | "warn" | "bad" }) => {
  const c = {
    neutral: "bg-surface-container text-on-surface-variant",
    good: "bg-tertiary-container text-tertiary",
    warn: "bg-amber-100 text-amber-800",
    bad: "bg-error-container text-error",
  }[tone];
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold whitespace-nowrap ${c}`}>{children}</span>;
};

const ProjectFullPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showPayload, setShowPayload] = useState(false);
  const { authUser } = useAuthStore();

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    axiosInstance
      .get(`/project/${id}/full`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.message || "Could not load this project."))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background grid place-items-center">
        <Loader2 size={22} className="animate-spin text-primary" />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-background grid place-items-center text-on-surface-variant text-sm">
        Project not found.
      </div>
    );
  }

  const p = data.project;
  const syncTone =
    p.sync.status === "Synced" ? "good"
    : p.sync.status === "Sync Failed" ? "bad"
    : ["Sync Uncertain", "Held — ERP Unavailable"].includes(p.sync.status) ? "warn"
    : "neutral";

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-6 pt-20 pb-12 space-y-4">
        {/* ── Header ── */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <button
              onClick={() => navigate("/projects")}
              className="inline-flex items-center gap-1 text-[12px] text-on-surface-variant hover:text-on-surface mb-1"
            >
              <ArrowLeft size={13} /> Projects
            </button>
            <h1 className="text-[24px] font-black text-on-surface leading-tight">{p.name}</h1>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap text-[12px] text-on-surface-variant">
              {p.pcrNumber && <span className="font-mono">{p.pcrNumber}</span>}
              <span>·</span>
              <span>{p.category}{p.subcategory ? ` / ${p.subcategory}` : ""}</span>
              {p.tenant && <><span>·</span><span>{p.tenant.name}</span></>}
              {p.isDraft && <Pill tone="warn">Draft</Pill>}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Pill tone={p.projectStatus === "Completed" ? "good" : "neutral"}>{p.projectStatus}</Pill>
            <Pill tone={syncTone}>{p.sync.status}</Pill>
          </div>
        </div>

        {/* ── Overview ── */}
        <Section icon={FileText} title="Project">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <Field label="Client" value={p.client?.name} />
            <Field label="Owner (BD)" value={p.owner?.name} />
            <Field label="Supervisor" value={p.supervisor?.name} />
            <Field label="Client tier" value={p.clientTier} />
            <Field label="Source" value={p.source} />
            <Field label="Lifecycle" value={p.lifecycleStatus} />
            <Field label="ETA" value={fmt(p.eta)} />
            <Field label="Created" value={fmt(p.createdAt)} />
          </div>
          {p.description && (
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-on-surface-variant mb-1">Description</div>
              <p className="text-[13px] text-on-surface whitespace-pre-wrap leading-relaxed">{p.description}</p>
            </div>
          )}
        </Section>

        {/* ── Commercial. Shown here, never sent to ERP. ── */}
        <Section icon={Coins} title="Commercial" meta="held in Omni Workspace only — none of this is sent to ERP">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Field label="Billing type" value={p.billingType} />
            <Field label="Project value" value={money(p.projectPrice)} />
            <Field label="Hourly rate" value={p.hourlyRate ? money(p.hourlyRate) : "—"} />
            <Field label="Estimated hours" value={p.estimatedHours || "—"} />
            <Field label="Escrow" value={money(p.escrowAmount)} />
            <Field label="Released" value={money(p.releasedPayment)} />
            <Field label="Upfront" value={money(p.upfrontPayment)} />
            <Field label="Payment status" value={p.paymentStatus} />
            <Field
              label="Commission"
              value={
                p.commissionAttribution === "company" ? (
                  <Pill tone="warn">Company direct — nobody credited</Pill>
                ) : (
                  "Credited to a BD"
                )
              }
            />
            <Field label="Target margin" value={p.targetProfitMarginPct ? `${p.targetProfitMarginPct}%` : "—"} />
          </div>
        </Section>

        {/* ── Delivery milestones ── */}
        <Section
          icon={ListChecks}
          title="Delivery milestones"
          meta={`${p.deliveryMilestones.length} · what gets built`}
        >
          {p.deliveryMilestones.length === 0 ? (
            <p className="text-[13px] text-on-surface-variant">None recorded.</p>
          ) : (
            <div className="space-y-3">
              {p.deliveryMilestones.map((m: any) => (
                <div key={m.id} className="rounded-lg border border-outline-variant p-3">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <span className="text-[13px] font-bold text-on-surface">
                        {m.order}. {m.title}
                      </span>
                      {m.description && (
                        <p className="text-[12px] text-on-surface-variant mt-0.5">{m.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-on-surface-variant">{fmt(m.dueDate)}</span>
                      <Pill tone={m.status === "Completed" ? "good" : m.status === "In Progress" ? "warn" : "neutral"}>
                        {m.status}
                      </Pill>
                    </div>
                  </div>
                  {m.deliverables.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-outline-variant/60">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-on-surface-variant mb-1">
                        Deliverables — {m.doneCount} of {m.totalCount}
                      </div>
                      <ul className="grid sm:grid-cols-2 gap-1">
                        {m.deliverables.map((d: any, i: number) => (
                          <li key={i} className="flex items-center gap-1.5 text-[12px] text-on-surface">
                            {d.done ? (
                              <CheckCircle2 size={13} className="text-tertiary shrink-0" />
                            ) : (
                              <Circle size={13} className="text-outline shrink-0" />
                            )}
                            <span className={d.done ? "" : "text-on-surface-variant"}>{d.title}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* ── Payment milestones ── */}
        <Section
          icon={Receipt}
          title="Payment milestones"
          meta={`${p.paymentMilestones.length} · when it is billed`}
        >
          {p.paymentMilestones.length === 0 ? (
            <p className="text-[13px] text-on-surface-variant">None recorded.</p>
          ) : (
            <div className="space-y-3">
              {p.paymentMilestones.map((m: any) => (
                <div key={m.id} className="rounded-lg border border-outline-variant p-3">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <span className="text-[13px] font-bold text-on-surface">
                        {m.order}. {m.title}
                      </span>
                      {m.description && <p className="text-[12px] text-on-surface-variant mt-0.5">{m.description}</p>}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[13px] font-bold text-on-surface">{money(m.amount)}</span>
                      <Pill tone={m.status === "Released" ? "good" : "neutral"}>{m.status}</Pill>
                      {m.paymentState && <Pill tone={m.paymentState === "Paid" ? "good" : "neutral"}>{m.paymentState}</Pill>}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2 pt-2 border-t border-outline-variant/60">
                    <Field label="Due" value={fmt(m.dueDate)} />
                    <Field label="Duration" value={m.duration} />
                    <Field label="Work status" value={m.workStatus} />
                    <Field label="ERP milestone id" value={m.erpMilestoneId} mono />
                  </div>
                  {m.scope && <Field label="Completion criteria" value={m.scope} />}
                  {m.notes && <div className="mt-2"><Field label="Notes" value={m.notes} /></div>}
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* ── Requirement checklist ── */}
        <Section icon={CheckCircle2} title="Requirement checklist" meta={`${p.requirementChecklist.length} items`}>
          <div className="space-y-2">
            {p.requirementChecklist.map((r: any) => (
              <div key={r.id} className="rounded-lg border border-outline-variant p-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <span className="text-[13px] font-semibold text-on-surface">{r.label}</span>
                    {r.required && <span className="text-error ml-1">*</span>}
                    {r.detail && <p className="text-[12px] text-on-surface-variant mt-0.5">{r.detail}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    {r.category && <Pill>{r.category}</Pill>}
                    <Pill tone={r.met ? "good" : "warn"}>{r.status}</Pill>
                  </div>
                </div>
                {(r.note || r.rejectionNote || r.acknowledged) && (
                  <div className="mt-2 pt-2 border-t border-outline-variant/60 space-y-1 text-[12px]">
                    {r.acknowledged && (
                      <p className="text-on-surface-variant">
                        Acknowledged{r.acknowledgedBy ? ` by ${r.acknowledgedBy}` : ""} · {fmtDT(r.acknowledgedAt)}
                      </p>
                    )}
                    {r.note && <p className="text-on-surface">{r.note}</p>}
                    {/* Kept visible: an item that was rejected and then fixed is
                        a different thing from one that was right first time. */}
                    {r.rejectionNote && (
                      <p className="text-error">Previously rejected: {r.rejectionNote}</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>

        {/* ── Documents ── */}
        <Section icon={Paperclip} title="Documents" meta={`${p.documents.length} files`}>
          {p.documents.length === 0 ? (
            <p className="text-[13px] text-on-surface-variant">No files uploaded.</p>
          ) : (
            <div className="space-y-2">
              {p.documents.map((d: any) => (
                <div key={d.id} className="flex items-center justify-between gap-3 rounded-lg border border-outline-variant p-2.5 flex-wrap">
                  <div className="min-w-0">
                    <span className="text-[13px] font-medium text-on-surface">{d.name}</span>
                    <p className="text-[11px] text-on-surface-variant mt-0.5">
                      {d.category} · {kb(d.size)} · {fmt(d.uploadedAt)}
                      {d.description ? ` · ${d.description}` : ""}
                    </p>
                  </div>
                  {d.url ? (
                    <a
                      href={d.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-[12px] font-semibold text-primary hover:underline shrink-0"
                    >
                      Open <ExternalLink size={12} />
                    </a>
                  ) : (
                    // Said plainly, because a file ERP cannot open is the exact
                    // problem this page exists to surface.
                    <span className="text-[11px] text-error font-semibold shrink-0">No link — ERP cannot open this</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* ── Project Requirements Document ──
            The PRD outlives project creation: it is approved, it goes stale
            when the project moves, and its ERP sync can need retrying. All of
            that has to be reachable from the project itself, not only from the
            creation wizard that happened to generate it. */}
        <Section icon={FileText} title="Project Requirements Document">
          <PrdSection
            projectId={String(p.id)}
            projectName={p.name}
            projectCategory={p.category || ""}
            ensureProject={async () => String(p.id)}
            canSeeCommercial={["Admin", "Supervisor", "Sales Director"].includes(authUser?.role || "")}
          />
        </Section>

        {/* ── Questionnaire ── */}
        {p.questionnaire && (
          <Section
            icon={MessageSquareText}
            title="Questionnaire"
            meta={`${p.questionnaire.responses.length} answers · ${p.questionnaire.isSubmitted ? "submitted " + fmt(p.questionnaire.submittedAt) : "not submitted"}`}
          >
            <div className="space-y-3">
              {p.questionnaire.responses.map((q: any) => (
                <div key={q.order} className="border-l-2 border-outline-variant pl-3">
                  <p className="text-[12px] font-semibold text-on-surface-variant">
                    {q.order}. {q.question}
                  </p>
                  <p className="text-[13px] text-on-surface mt-0.5 whitespace-pre-wrap">
                    {Array.isArray(q.answer) ? q.answer.join(", ") : String(q.answer ?? "—")}
                  </p>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* ── Proposal ── */}
        {p.proposal && (
          <Section icon={Sparkles} title="Proposal">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <span className="text-[13px] font-bold text-on-surface">{p.proposal.title}</span>
                <p className="text-[11px] text-on-surface-variant mt-0.5">
                  v{p.proposal.version} · {p.proposal.status}
                  {p.proposal.publishedAt ? ` · published ${fmt(p.proposal.publishedAt)}` : ""}
                </p>
              </div>
              {p.proposal.url && (
                <a href={p.proposal.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[12px] font-semibold text-primary hover:underline">
                  Open <ExternalLink size={12} />
                </a>
              )}
            </div>
          </Section>
        )}

        {/* ── Invoices ── */}
        <Section icon={Receipt} title="Invoices" meta={`${data.invoices.length}`}>
          {data.invoices.length === 0 ? (
            <p className="text-[13px] text-on-surface-variant">None raised.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="text-on-surface-variant">
                  <tr>{["Invoice", "Milestone", "Amount", "Paid", "Due", "Status"].map((h) => (
                    <th key={h} className="text-left font-bold uppercase tracking-wider text-[10px] pb-2">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-outline-variant">
                  {data.invoices.map((i: any) => (
                    <tr key={i.id}>
                      <td className="py-2 font-mono">{i.number}</td>
                      <td className="py-2 text-on-surface-variant">{i.milestoneTitle || "—"}</td>
                      <td className="py-2 font-semibold">{money(i.amount, i.currency)}</td>
                      <td className="py-2">{money(i.paidAmount, i.currency)}</td>
                      <td className="py-2">{fmt(i.dueDate)}</td>
                      <td className="py-2"><Pill tone={i.status === "Completed" ? "good" : "warn"}>{i.status}</Pill></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        {/* ── Client actions ── */}
        {data.clientActions.length > 0 && (
          <Section icon={Users} title="Client actions" meta={`${data.clientActions.length}`}>
            <div className="space-y-2">
              {data.clientActions.map((a: any) => (
                <div key={a.id} className="flex items-start justify-between gap-3 rounded-lg border border-outline-variant p-2.5 flex-wrap">
                  <div className="min-w-0">
                    <span className="text-[13px] font-medium text-on-surface">{a.title}</span>
                    <p className="text-[11px] text-on-surface-variant mt-0.5">
                      {a.type} · due {fmt(a.dueDate)}{a.stage ? ` · ${a.stage}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Pill tone={a.priority === "Blocking" ? "bad" : a.priority === "Required" ? "warn" : "neutral"}>{a.priority}</Pill>
                    <Pill tone={a.status === "Completed" ? "good" : "neutral"}>{a.status}</Pill>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* ── Commissions ── */}
        {data.commissions.length > 0 && (
          <Section icon={Coins} title="Commissions" meta={`${data.commissions.length}`}>
            <div className="space-y-1.5">
              {data.commissions.map((c: any) => (
                <div key={c.id} className="flex items-center justify-between gap-3 text-[12px]">
                  <span className="text-on-surface">{c.milestoneTitle} · <b>{c.bd}</b></span>
                  <span className="flex items-center gap-2">
                    <span className="text-on-surface-variant">on {money(c.releasedAmount)}</span>
                    <b className="text-on-surface">PKR {c.commissionAmount.toLocaleString()}</b>
                    <Pill tone={c.status === "Paid" ? "good" : "neutral"}>{c.status}</Pill>
                  </span>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* ── ERP ── */}
        <Section icon={Server} title="ERP synchronisation">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <Field label="ERP project id" value={p.erp.projectId} mono />
            <Field label="ERP status" value={p.erp.status} />
            <Field label="Progress" value={`${p.erp.progress}%`} />
            <Field label="Last sync" value={fmtDT(p.erp.lastSyncAt)} />
            <Field label="Sync state" value={<Pill tone={syncTone}>{p.sync.status}</Pill>} />
            <Field label="Attempts" value={p.sync.retryCount} />
            <Field label="Connection at last attempt" value={p.sync.lastConnectionState} />
            <Field label="Idempotency key" value={p.sync.idempotencyKey} mono />
          </div>

          {p.sync.lastError && (
            <div className="rounded-lg bg-error-container/30 border border-error/30 p-3 mb-4">
              <p className="text-[12px] font-bold text-error flex items-center gap-1.5">
                <AlertTriangle size={13} /> Last error
                {p.sync.lastErrorCode ? ` · ${p.sync.lastErrorCode}` : ""}
              </p>
              <p className="text-[12px] text-on-surface mt-1">{p.sync.lastError}</p>
            </div>
          )}

          {data.syncEvents.length > 0 && (
            <div className="overflow-x-auto mb-4">
              <table className="w-full text-[11px]">
                <thead className="text-on-surface-variant">
                  <tr>{["When", "Direction", "Event", "By", "Connection", "Status", "Error"].map((h) => (
                    <th key={h} className="text-left font-bold uppercase tracking-wider text-[10px] pb-2">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-outline-variant">
                  {data.syncEvents.map((e: any) => (
                    <tr key={e.id}>
                      <td className="py-1.5 whitespace-nowrap">{fmtDT(e.at)}</td>
                      <td className="py-1.5 whitespace-nowrap">{e.direction}</td>
                      <td className="py-1.5">{e.event}</td>
                      <td className="py-1.5">{e.user || "—"}</td>
                      <td className="py-1.5">{e.connectionState || "—"}</td>
                      <td className="py-1.5">
                        <Pill tone={e.status === "Processed" ? "good" : "bad"}>{e.status}</Pill>
                      </td>
                      <td className="py-1.5 text-error max-w-[220px] truncate" title={e.error}>
                        {e.errorCode || e.error || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* The payload itself. Built by the same function that sends it, so
              what is shown here is what ERP receives — not a description of it. */}
          <div className="border-t border-outline-variant pt-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="text-[12px] font-bold text-on-surface flex items-center gap-1.5">
                  <Code2 size={13} /> Exactly what ERP receives
                </p>
                <p className="text-[11px] text-on-surface-variant mt-0.5">
                  Generated by the same code that sends it. No client identity and no monetary figure is included.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowPayload((v) => !v)}
                  className="px-3 py-1.5 rounded-lg border border-outline-variant text-[12px] font-semibold text-on-surface hover:bg-surface-container-low"
                >
                  {showPayload ? "Hide" : "Show"} payload
                </button>
                <button
                  onClick={() => {
                    const blob = new Blob([JSON.stringify(data.erpPayload, null, 2)], { type: "application/json" });
                    const a = document.createElement("a");
                    a.href = URL.createObjectURL(blob);
                    a.download = `erp-payload-${p.pcrNumber || p.id}.json`;
                    a.click();
                    URL.revokeObjectURL(a.href);
                  }}
                  disabled={!data.erpPayload}
                  className="px-3 py-1.5 rounded-lg border border-outline-variant text-[12px] font-semibold text-on-surface hover:bg-surface-container-low disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                  <Download size={12} /> JSON
                </button>
              </div>
            </div>

            {data.erpPayloadError && (
              <p className="text-[12px] text-error mt-2">Could not build the payload: {data.erpPayloadError}</p>
            )}

            {showPayload && data.erpPayload && (
              <pre className="mt-3 p-3 rounded-lg bg-surface-container-low border border-outline-variant text-[10px] font-mono overflow-x-auto max-h-[500px] overflow-y-auto">
                {JSON.stringify(data.erpPayload, null, 2)}
              </pre>
            )}
          </div>
        </Section>

        <p className="text-[11px] text-on-surface-variant flex items-center gap-1.5">
          <Clock size={11} /> Last updated {fmtDT(p.updatedAt)}
        </p>
      </div>
    </div>
  );
};

export default ProjectFullPage;
